import { ReactNode, useMemo } from 'react'
import { useDropzone, FileRejection, DropEvent, DropzoneOptions } from 'react-dropzone'
import { Box, BoxProps, Typography } from '@mui/material'
import FileDropzoneEmptyState from './FileDropzoneEmptyState'
import { ColorChip } from '../Chips'
import { getStandard } from '../../../services/getStandard'
import { EStandard } from '../../../services/enums/EStandard'

interface Accept {
    [key: string]: string[]
}

interface FileDropzoneProps {
    onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void
    filesAccepted?: Accept
    selectedFile?: File
    fileType?: string
    dropzoneOptions?: DropzoneOptions
    previewComponent: ReactNode
    selectedFileContent?: string
}

export const FileDropzone = ({
    onDrop,
    filesAccepted,
    selectedFile,
    selectedFileContent,
    fileType = '',
    previewComponent,
    dropzoneOptions,
    ...boxProps
}: FileDropzoneProps & BoxProps): JSX.Element => {
    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles, fileRejections, event) => {
            if (acceptedFiles.length > 0) {
                onDrop?.(acceptedFiles, fileRejections, event)
            }
        },
        accept: filesAccepted,
        noClick: !!selectedFile,
        ...dropzoneOptions,
    })

    const standard = useMemo(() => selectedFileContent && getStandard(selectedFileContent), [selectedFileContent])

    return (
        <Box sx={{ height: '100%' }}>
            <Box
                {...getRootProps({ className: 'dropzone' })}
                {...boxProps}
                sx={{
                    border: (theme) => `1px dashed ${theme.palette.secondary.light}`,
                    borderRadius: '8px',
                    p: 2,
                    ...boxProps.sx,
                    ...(!selectedFileContent && { height: '100%' }),
                }}
            >
                <input {...getInputProps()} />
                {selectedFile ? previewComponent : <FileDropzoneEmptyState fileType={fileType} />}
            </Box>
            {selectedFileContent && (
                <Box sx={{ height: '70%', mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='h6' gutterBottom>
                            Preview of "{selectedFile?.name}"
                        </Typography>
                        {standard && <StandardChip standard={standard} />}
                    </Box>
                    <Box
                        sx={{
                            m: 2,
                            p: 2,
                            backgroundColor: '#F8F8F8',
                            maxHeight: '100%',
                            overflow: 'scroll',
                        }}
                    >
                        <pre>{selectedFile && selectedFileContent}</pre>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export function StandardChip({ standard }: { standard: EStandard }): JSX.Element {
    if (standard === EStandard.telemetry) {
        return <ColorChip label='Telemetry' color='green' width='fit-content' />
    } else if (standard === EStandard.sdCard) {
        return <ColorChip label='SD Card' color='blue' width='fit-content' />
    } else {
        return <ColorChip label='Unknown' color='gray' width='fit-content' />
    }
}
